'use client';

import { useEffectEvent } from "@floating-ui/react/utils";
import Color from 'color';
import React, { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersXYZ";

interface ClickSparkProps {
	readonly sparkColor?: string;
	readonly sparkColorBrightnessPlusMinusDecimal?: number;
	readonly sparkColorHuePlusMinusDeg?: number;
	readonly sparkSize?: number;
	readonly sparkSizePlusMinus?: number;
	readonly sparkRadius?: number;
	readonly sparkRadiusPlusMinus?: number;
	readonly sparkCount?: number;
	readonly sparkCountPlusMinus?: number;
	readonly duration?: number;
	readonly easing?: "linear" | "ease-in" | "ease-out" | "ease-in-out";
	readonly extraScale?: number;
	readonly children?: React.ReactNode;
}

interface Spark {
	x: number;
	y: number;
	angle: number;
	color: InstanceType<typeof Color>;
	startTime: number;
	speed: number;
	size: number;
	radius: number;
}

type SingleXYData = { x: number; y: number; identifier: number; };
type XYDataMap = Map<number, SingleXYData>;

export function ClickSpark({
	sparkColor = "#4dc0cf",
	sparkColorBrightnessPlusMinusDecimal = 0.4,
	sparkColorHuePlusMinusDeg = 70,
	sparkSize = 7,
	sparkSizePlusMinus = 5,
	sparkRadius = 33,
	sparkRadiusPlusMinus = 10,
	sparkCount = 13,
	sparkCountPlusMinus = 3,
	duration = 400,
	easing = "ease-out",
	extraScale = 1.0,
	children
}: ClickSparkProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const sparksRef = useRef<Spark[]>([]);
	const startTimeRef = useRef<number | null>(null);

	const prefersReducedMotion = usePrefersReducedMotion();

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const parent = canvas.parentElement;
		if (!parent) return;

		let resizeTimeout: NodeJS.Timeout;

		const resizeCanvas = () => {
			const { width, height } = parent.getBoundingClientRect();
			if (canvas.width !== width || canvas.height !== height) {
				canvas.width = width;
				canvas.height = height;
			}
		};

		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(resizeCanvas, 100);
		};

		const ro = new ResizeObserver(handleResize);
		ro.observe(parent);

		resizeCanvas();

		return () => {
			ro.disconnect();
			clearTimeout(resizeTimeout);
		};
	}, []);


	const easeFunc = useEffectEvent((t: number) => {
		switch (easing) {
			case "linear":
				return t;
			case "ease-in":
				return t * t;
			case "ease-in-out":
				return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
			default:
				return t * (2 - t) ** .5;
		}
	});

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		let animationId: number;

		const draw = (timestamp: number) => {
			if (!startTimeRef.current) startTimeRef.current = timestamp;
			ctx?.clearRect(0, 0, canvas.width, canvas.height);

			sparksRef.current = sparksRef.current.filter((spark: Spark) => {
				const elapsed = timestamp - spark.startTime;
				const finalDuration = duration / spark.speed * (spark.size / sparkSize);

				if (elapsed >= finalDuration) return false;

				const progress = elapsed / finalDuration;
				const eased = easeFunc(progress);

				const distance = eased * spark.radius * extraScale;
				const lineLength = spark.size * (1 - eased);

				const x1 = spark.x + distance * Math.cos(spark.angle);
				const y1 = spark.y + distance * Math.sin(spark.angle);
				const x2 = spark.x + (distance + lineLength) * Math.cos(spark.angle);
				const y2 = spark.y + (distance + lineLength) * Math.sin(spark.angle);

				const progressPast90Percent = Math.min(1, (elapsed - finalDuration * 0.9) / (finalDuration * 0.1));

				ctx.strokeStyle = spark.color.fade(progressPast90Percent).hexa();
				ctx.lineWidth = 2;
				ctx.beginPath();
				ctx.moveTo(x1, y1);
				ctx.lineTo(x2, y2);
				ctx.stroke();

				return true;
			});

			animationId = requestAnimationFrame(draw);
		};

		animationId = requestAnimationFrame(draw);

		return () => {
			cancelAnimationFrame(animationId);
		};
	}, [sparkColor, sparkSize, sparkRadius, sparkCount, duration, extraScale, easeFunc]);

	const mouseIsDownRef = useRef(false);
	const MOVE_INTERVAL_PX = 10;
	const mouseMovePXBuiltUpRef = useRef<Map<number, number>>(new Map());

	const lastXYsRef = useRef<XYDataMap>(new Map());
	function getXYs(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): XYDataMap {
		const res = getXYsRaw(e);
		lastXYsRef.current = res;

		mouseMovePXBuiltUpRef.current.keys().forEach(i => {
			if (!res.has(i)) mouseMovePXBuiltUpRef.current.delete(i);
		});

		return res;
	}


	function getXYsRaw(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): XYDataMap {
		if (!mouseIsDownRef.current) return new Map();
		if ('clientX' in e) {
			return new Map([[-1, { x: e.clientX, y: e.clientY, identifier: -1 }]]);
		} else if ('touches' in e) {
			const map = new Map<number, SingleXYData>();
			for (let i = 0; i < e.touches.length; i++) {
				const touch = e.touches[i]!;
				map.set(touch.identifier, { x: touch.clientX, y: touch.clientY, identifier: touch.identifier });
			}
			return map;
		} else {
			throw new Error("Unsupported event type");
		}
	}

	const createSparks = useEffectEvent((pos: { x: number, y: number; }, individualSparkChance: number) => {
		if (prefersReducedMotion) return;
		const canvas = canvasRef.current;
		if (!canvas) return;
		const rect = canvas.getBoundingClientRect();
		const x = pos.x - rect.left;
		const y = pos.y - rect.top;

		const sparksToMake = sparkCount + Math.floor(Math.random() * sparkCountPlusMinus);

		const now = performance.now();
		const newSparks: Spark[] = Array.from({ length: sparksToMake }, (_, i): Spark => ({
			x,
			y,
			angle: (2 * Math.PI * (i + (5 * (Math.random() ** 2)))) / sparksToMake,
			color: Color(sparkColor)
				.lighten(sparkColorBrightnessPlusMinusDecimal * (Math.random() - 0.5))
				.rotate(sparkColorHuePlusMinusDeg * (Math.random() - 0.5)),
			startTime: now,
			speed: 0.8 + Math.random() * 0.4,
			size: sparkSize + (Math.random() * sparkSizePlusMinus - (sparkSizePlusMinus / 2)),
			radius: sparkRadius + (Math.random() * sparkRadiusPlusMinus - (sparkRadiusPlusMinus / 2))
		}));

		sparksRef.current.push(...newSparks.filter(() => Math.random() < individualSparkChance));
	});

	function processXYs(previousXYs: XYDataMap, newXYs: XYDataMap) {
		for (const newXY of newXYs.values()) {
			const previousXY = previousXYs.get(newXY.identifier);

			if (!previousXY) {
				createSparks(newXY, 1.0);
				continue;
			}

			const diffX = newXY.x - previousXY.x;
			const diffY = newXY.y - previousXY.y;
			const newDistance = Math.hypot(diffX, diffY);
			const distance = (mouseMovePXBuiltUpRef.current.get(newXY.identifier) ?? 0) + newDistance;
			const sparkCreationRuns = Math.floor(distance / MOVE_INTERVAL_PX);
			mouseMovePXBuiltUpRef.current.set(newXY.identifier, distance - (sparkCreationRuns * MOVE_INTERVAL_PX));
			for (let j = 0; j < sparkCreationRuns; j++) {
				const oldFactor = 1 - (j / sparkCreationRuns);
				const createAtClientX = (previousXY.x * oldFactor) + (newXY.x * (1 - oldFactor));
				const createAtClientY = (previousXY.y * oldFactor) + (newXY.y * (1 - oldFactor));
				createSparks({
					x: createAtClientX,
					y: createAtClientY
				}, 0.10);
			}
		}
	}


	const handleMouseDown = useEffectEvent((e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>): void => {
		if ('pointerType' in e && e.pointerType !== 'mouse') return;
		mouseIsDownRef.current = true;
		const previousXYs = lastXYsRef.current;
		const newXYs = getXYs(e);
		processXYs(previousXYs, newXYs);
	});

	const handleMouseUp = useEffectEvent((e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>): void => {
		if ('pointerType' in e && e.pointerType !== 'mouse') return;
		mouseIsDownRef.current = false;
		const previousXYs = lastXYsRef.current;
		const newXYs = getXYs(e);
		processXYs(previousXYs, newXYs);
	});

	const handleMouseMove = useEffectEvent((e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>): void => {
		if ('clientX' in e && 'pointerType' in e && e.pointerType !== 'mouse') return;
		const previousXYs = lastXYsRef.current;
		const newXYs = getXYs(e);
		processXYs(previousXYs, newXYs);
	});


	return <div style={{
		position: "relative"
	}}
		onTouchStart={handleMouseDown}
		onTouchMove={handleMouseMove}
		onTouchEnd={handleMouseUp}
		onTouchCancel={handleMouseUp}
		onPointerDown={handleMouseDown}
		onPointerMove={handleMouseMove}
		onPointerUp={handleMouseUp}
		onPointerLeave={handleMouseUp}
		onPointerCancel={handleMouseUp}
	>
		<canvas
			ref={canvasRef}
			style={{
				position: "absolute",
				inset: 0,
				pointerEvents: "none",
				zIndex: 99999999999999,
			}}
		/>
		{children}
	</div>;
};
